import {
  type ColumnFormat,
  isNumberFormat,
} from "@repo/shared/types/column-format";
import { z } from "zod";

export const OperationTypeSchema = z
  .enum([
    "CONCAT",
    "COUNT",
    "COUNT_VALUE",
    "COUNT_EMPTY",
    "COUNT_FALSE",
    "COUNT_TRUE",
    "COUNT_UNIQUE",
    "MAX",
    "MEAN",
    "MEDIAN",
    "MIN",
    "PERCENTAGE_EMPTY",
    "PERCENTAGE_FALSE",
    "PERCENTAGE_NON_EMPTY",
    "PERCENTAGE_TRUE",
    "SUM",
    "VARIANCE",
  ])
  .describe(
    "Aggregation operation. Numeric: SUM, MEAN, MEDIAN, MIN, MAX, VARIANCE. Counting: COUNT, COUNT_VALUE, COUNT_UNIQUE, COUNT_EMPTY. Boolean: COUNT_TRUE, COUNT_FALSE. Text: CONCAT.",
  );
export type OperationType = z.infer<typeof OperationTypeSchema>;

export type OperationOption = { value: OperationType; label: string };

/** Canonical human-readable labels for every operation type. */
export const OPERATION_LABELS: Record<OperationType, string> = {
  CONCAT: "Concat",
  COUNT: "Count",
  COUNT_VALUE: "Count values",
  COUNT_EMPTY: "Count empty",
  COUNT_FALSE: "Count false",
  COUNT_TRUE: "Count true",
  COUNT_UNIQUE: "Count unique",
  MAX: "Max",
  MEAN: "Average",
  MEDIAN: "Median",
  MIN: "Min",
  PERCENTAGE_EMPTY: "Percent empty",
  PERCENTAGE_FALSE: "Percent false",
  PERCENTAGE_NON_EMPTY: "Percent non-empty",
  PERCENTAGE_TRUE: "Percent true",
  SUM: "Sum",
  VARIANCE: "Variance",
};

/** Builds an `OperationOption[]` from operation types, using `OPERATION_LABELS` with optional overrides for contextual labels. */
const buildOptions = (
  types: OperationType[],
  overrides?: Partial<Record<OperationType, string>>,
): OperationOption[] =>
  types.map((value) => ({
    value,
    label: overrides?.[value] ?? OPERATION_LABELS[value],
  }));

const UNIVERSAL_TYPES: OperationType[] = [
  "COUNT",
  "COUNT_VALUE",
  "COUNT_EMPTY",
  "COUNT_UNIQUE",
  "PERCENTAGE_EMPTY",
  "PERCENTAGE_NON_EMPTY",
];

const NUMERIC_TYPES: OperationType[] = [
  "SUM",
  "MEAN",
  "MEDIAN",
  "MIN",
  "MAX",
  "VARIANCE",
];

const BOOLEAN_TYPES: OperationType[] = [
  "COUNT_TRUE",
  "COUNT_FALSE",
  "PERCENTAGE_TRUE",
  "PERCENTAGE_FALSE",
];

const STRING_TYPES: OperationType[] = ["MIN", "MAX", "CONCAT"];

const STRING_LABEL_OVERRIDES: Partial<Record<OperationType, string>> = {
  MIN: "First",
  MAX: "Last",
};

const DATE_TYPES: OperationType[] = ["MIN", "MAX"];

const DATE_LABEL_OVERRIDES: Partial<Record<OperationType, string>> = {
  MIN: "Earliest",
  MAX: "Latest",
};

/** Operations available for all column types. */
export const UNIVERSAL_OPERATIONS: OperationOption[] =
  buildOptions(UNIVERSAL_TYPES);

/** Operations available only for numeric columns. */
export const NUMERIC_OPERATIONS: OperationOption[] =
  buildOptions(NUMERIC_TYPES);

/** Operations available only for boolean columns. */
export const BOOLEAN_OPERATIONS: OperationOption[] =
  buildOptions(BOOLEAN_TYPES);

/** Operations available for string/text columns. */
export const STRING_OPERATIONS: OperationOption[] = buildOptions(
  STRING_TYPES,
  STRING_LABEL_OVERRIDES,
);

/** Operations available for date/time columns. */
export const DATE_OPERATIONS: OperationOption[] = buildOptions(
  DATE_TYPES,
  DATE_LABEL_OVERRIDES,
);

/** Operations whose numeric output supports period-over-period delta display. */
export const DELTA_ELIGIBLE_OPERATIONS = new Set<OperationType>([
  "COUNT",
  "COUNT_VALUE",
  "COUNT_EMPTY",
  "COUNT_UNIQUE",
  "COUNT_TRUE",
  "COUNT_FALSE",
  "SUM",
  "MEAN",
  "MEDIAN",
  "MIN",
  "MAX",
  "VARIANCE",
]);

/** Returns the list of allowed operations based on a column's format. */
export const getOperationsForType = (
  format: ColumnFormat | undefined,
): OperationOption[] => {
  const ops: OperationOption[] = [];

  const isNumber = isNumberFormat(format);
  const isBoolean = format?.kind === "BOOLEAN";
  const isDate = format?.kind === "DATE";

  if (isNumber) ops.push(...NUMERIC_OPERATIONS);
  if (isBoolean) ops.push(...BOOLEAN_OPERATIONS);
  if (isDate) ops.push(...DATE_OPERATIONS);
  if (!isNumber && !isBoolean && !isDate) ops.push(...STRING_OPERATIONS);

  ops.push(...UNIVERSAL_OPERATIONS);
  return ops;
};
